/* eslint-disable import/no-named-as-default */
/* eslint-disable react/jsx-handler-names */
import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { useIsUserLogged, countryToggleConfig } from '@spotahome/ui-library';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import { landlordPoliciesContainFees } from '@spotahome/marketplace-common/src/utils/landlordPolicies';

import { isDateValid } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { useSoyuzExperiment } from '@spotahome/soyuz/client';

import ListingTranslateContent from './ListingTranslateContent';
import ListingDetails from './ListingDetails';
import ListingFlatmates from './ListingFlatmates';
import ListingHighlights from './ListingHighlights';
import ListingPricing from './ListingPricing';
import ListingAvailabilities from './ListingAvailabilities';
import ListingSurroundings from './ListingSurroundings';
import ListingHowToBook from './ListingHowToBook';
import PropertyRules from './PropertyRules';
import PropertyRulesRedesign from './PropertyRulesRedesign';
import Reviews from './Reviews';
import ExtendedLandlordInformation from './ExtendedLandlordInformation';
import BillsIncludedModal from './BillsIncludedModal';
import CancellationPolicies from './CancellationPolicies';
import CancellationPoliciesRedesign from './CancellationPoliciesRedesign';
import ListingPaymentTimeline from './ListingPaymentTimeline/ListingPaymentTimeline';

import './ListingInformation.scss';
import ListingOverview from './ListingOverview';

export const ListingInformation = ({
  roomDescription = '',
  landlordHomecards = [],
  contents,
  isRoomsAggregator,
  locale,
  listing,
  hasRoomDescriptionPricing = false,
  ...props
}) => {
  const isUserLogged = useIsUserLogged();
  const cancellationPoliciesRedesign = useSoyuzExperiment(
    'CancellationPoliciesRedesign'
  );
  const revampListingFeesAndExpenses = useSoyuzExperiment(
    'RevampListingFeesAndExpenses'
  );

  const isListingOverviewRevamp =
    useSoyuzExperiment('ListingOverviewRevamp').get('behaviour') === 'variant';
  const revampPropertyRules = useSoyuzExperiment('RevampPropertyRules');

  const isRevampListingFeesAndExpensesExperiment =
    countryToggleConfig(listing.countryCode).isUnderFeesAndExpensesExperiment &&
    revampListingFeesAndExpenses.get('behaviour') === 'variant';

  const hasDates =
    props.moveIn &&
    props.moveOut &&
    isDateValid(props.moveIn) &&
    isDateValid(props.moveOut);

  const landlordPoliciesHasFees = landlordPoliciesContainFees(
    contents.landlordPolicies
  );
  const moveLandlordPoliciesToPricing = countryToggleConfig(
    props.countryCode
  ).moveLandlordPoliciesToPricing;

  return (
    <Fragment>
      <div className="listing-information">
        {props.mustShowLanguageToggle && (
          <ListingTranslateContent
            isManualTranslated={contents.isManualTranslated}
            isTranslationNeeded={contents.isTranslationNeeded}
            onChangeLanguage={props.onLanguageChange}
            showTranslatedContent={props.showTranslatedContent}
          />
        )}

        {isListingOverviewRevamp ? (
          <ListingOverview
            description={contents.description}
            landlordDescription={contents.landlordDescription}
            details={contents.details}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={props.isWholeApartment}
            listingId={props.listingId}
            listing={listing}
            propertyFeatures={props.propertyFeatures}
            propertyFeaturesOldModel={props.propertyFeaturesOldModel}
            roomsInApartment={props.roomsInApartment}
            locale={locale}
            roomDescription={roomDescription}
            hasRoomDescriptionPricing={hasRoomDescriptionPricing}
          />
        ) : (
          <ListingDetails
            description={contents.description}
            landlordDescription={contents.landlordDescription}
            details={contents.details}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={props.isWholeApartment}
            listingId={props.listingId}
            listing={listing}
            propertyFeatures={props.propertyFeatures}
            propertyFeaturesOldModel={props.propertyFeaturesOldModel}
            roomsInApartment={props.roomsInApartment}
            locale={locale}
            roomDescription={roomDescription}
            hasRoomDescriptionPricing={hasRoomDescriptionPricing}
          />
        )}

        {(contents.whatWeLiked || contents.thingsToKeepInMind) && (
          <ListingHighlights
            whatWeLiked={contents.whatWeLiked}
            thingsToKeepInMind={contents.thingsToKeepInMind}
          />
        )}

        {props.hasReviews && <Reviews reference={props.listingId} />}

        {!isRoomsAggregator && isRevampListingFeesAndExpensesExperiment && (
          <ListingPaymentTimeline listing={listing} />
        )}

        {!isRoomsAggregator && !isRevampListingFeesAndExpensesExperiment && (
          <ListingPricing
            cityId={props.cityId}
            bills={props.bills}
            contractType={props.contractType}
            currencyLabel={props.currencyLabel}
            pricing={props.pricing}
            roomDescription={roomDescription}
            listingType={listing.type}
            m2={listing.m2}
            hasDates={!!hasDates}
            titleKey={'listing.pricing.title.standalone'}
            landlordPolicies={
              moveLandlordPoliciesToPricing &&
              landlordPoliciesHasFees &&
              contents.landlordPolicies
            }
            servicesAndExpenses={{
              periodicCleaning: props.propertyFeatures?.periodicCleaning
            }}
            isLogged={isUserLogged}
            listingId={props.listingId}
            listing={listing}
            moveIn={props.moveIn}
            moveOut={props.moveOut}
            deviceType={props.deviceType}
            hasHiddenFees={listing.hasHiddenFees}
            hasRoomDescriptionPricing={hasRoomDescriptionPricing}
          />
        )}

        {cancellationPoliciesRedesign.get('behaviour') === 'variant' ? (
          <CancellationPoliciesRedesign />
        ) : (
          <CancellationPolicies />
        )}

        {listing.propertyRules &&
        listing.documentsPolicy &&
        listing.moveInMoveOutPolicy &&
        listing.contractualPermissions &&
        revampPropertyRules.get('behaviour') === 'variant' ? (
          <PropertyRulesRedesign
            propertyRules={listing.propertyRules}
            documentsPolicy={listing.documentsPolicy}
            moveInMoveOutPolicy={listing.moveInMoveOutPolicy}
            contractualPermissions={listing.contractualPermissions}
            currency={props.currencyLabel}
          />
        ) : (
          <PropertyRules
            landlordPolicies={
              (!moveLandlordPoliciesToPricing || !landlordPoliciesHasFees) &&
              contents.landlordPolicies
            }
            landlordRules={props.landlordRules}
            landlordRulesInfo={props.landlordRulesInfo}
            cityId={props.cityId}
            countryCode={props.countryCode}
          />
        )}
        <ExtendedLandlordInformation
          currentListingId={listing.id}
          landlordHomecards={landlordHomecards}
          landlordScoring={listing.landlordScoring}
        />
        <ListingHowToBook
          hasRentCollection={listing?.bookingConditions?.hasRentCollection}
          hasVetting={listing?.bookingConditions?.hasVetting}
        />
        {!isRoomsAggregator && (
          <ListingAvailabilities
            availableFrom={props.availableFrom}
            dateUpdated={props.refreshAvailabilitiesDate}
            unavailabilities={props.unavailabilities}
            seasonalPrices={props.seasonalPrices}
            currency={props.currencySymbol}
            minStay={props.minStay}
            maxStay={props.maxStay}
            bookingTooEarlyDays={props.stayRules?.bookingTooEarlyDays}
            titleKey={'listing.availabilities.title'}
          />
        )}

        {!!contents.transportation && (
          <ListingSurroundings
            transportationDescription={contents.transportation}
          />
        )}

        {!!contents.flatmates && (
          <ListingFlatmates flatmatesDescription={contents.flatmates} />
        )}
      </div>
      <BillsIncludedModal
        bills={props.bills}
        listingType={listing.type}
        currency={props.currencySymbol}
      />
    </Fragment>
  );
};

ListingInformation.propTypes = {
  listing: PropTypes.shape(ListingShape).isRequired,
  cityId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  contents: PropTypes.shape({
    transportationDescription: PropTypes.string,
    flatmates: PropTypes.string,
    landlordPolicies: PropTypes.string,
    whatWeLiked: PropTypes.string,
    thingsToKeepInMind: PropTypes.string,
    isManualTranslated: PropTypes.bool,
    isTranslationNeeded: PropTypes.bool
  }).isRequired,
  landlordRules: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    })
  }).isRequired,
  landlordRulesInfo: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      value: PropTypes.string,
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  listingId: PropTypes.string.isRequired,
  hasReviews: PropTypes.bool.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  showTranslatedContent: PropTypes.bool.isRequired,
  mustShowLanguageToggle: PropTypes.bool.isRequired,
  roomDescription: PropTypes.string,
  // ...ListingPricingAvailability.propTypes,
  ...ListingDetails.propTypes,
  deviceType: PropTypes.string.isRequired,
  landlordHomecards: PropTypes.arrayOf(PropTypes.object),
  hasRoomDescriptionPricing: PropTypes.bool
};

export default ListingInformation;
